import { yupResolver } from '@hookform/resolvers/yup';
import Close from '@mui/icons-material/Close';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useRefreshMutation } from '@/api/authApiSlice';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { useChangeBranchMutation } from '@/api/common';
import { apiSlice } from '@/api/apiSlice';
import AppDropDownMenu from '../form-inputs/AppDropDownMenu';
import { useDispatch, useSelector } from 'react-redux';
import { selectMyInfo } from '@/pages/auth/authSlice';
import { useGetUser } from '@/hooks/useGetUser';
import { useNavigate } from 'react-router-dom';

interface Props {
	setChangeUnitOpen: any;
	changeUnitOpen: boolean;
}

const ChangeUnitModal = (props: Props) => {
	const { changeUnitOpen, setChangeUnitOpen } = props;
	const { t } = useTranslation();
	const [changeBranch] = useChangeBranchMutation();
	const userInfo = useSelector(selectMyInfo);
	const [refresh] = useRefreshMutation();
	const { userInfoRefetch } = useGetUser();
	const dispatch = useDispatch();

	const navigate = useNavigate();

	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		branchId: Yup.string().required(t('errorMessage.required')),
	});

	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const handleClose = () => {
		methods.reset({});
		setChangeUnitOpen(false);
	};

	const onSubmit: SubmitHandler<any> = async (data) => {
		const response = await changeBranch(data);
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateUnitSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
			const res = await refresh();
			if ('data' in res) {
				// Every endpoint answers for the unit in the bearer token - summaries,
				// counts, the notification bell - so once the token is the new unit's,
				// nothing already cached belongs to the staff member any more. Drop the
				// whole cache rather than naming tags: a query missed off that list
				// would keep serving the previous unit's records with no sign of it.
				dispatch(apiSlice.util.resetApiState());
				userInfoRefetch();

				navigate('/', { replace: true });
			}
		}
	};

	return (
		<>
			<Dialog
				open={changeUnitOpen}
				onClose={handleClose}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='xs'
				fullWidth
				onKeyDown={(e) => {
					if (e.key === 'Tab') {
						e.stopPropagation();
					}
				}}
			>
				<DialogTitle variant='h5'>{t('common.selectUnitToManage')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<Typography variant='body2' color='common.neutral40' padding={'0 24px'}>
					{t('common.selectUnitText')}
				</Typography>
				<DialogContent>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Grid container spacing={2} marginTop={0} marginBottom={2}>
								<Grid size={12}>
									<AppDropDownMenu
										control={methods.control}
										name='branchId'
										label={t('user.unit') + '*'}
										options={(userInfo?.branchList || []).map((item: any) => {
											return {
												id: item.id,
												name: item.displayName,
											};
										})}
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button sx={{ textTransform: 'none' }} onClick={() => handleClose()}>
						{t('button.cancel')}
					</Button>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onSubmit)}
					>
						{t('button.confirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default ChangeUnitModal;
